import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BadgeCheck, Download, Search, Loader2, AlertTriangle, Mail } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { getToken } from '../services/tokenService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/registre-adherents-ftpe`;
const h = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fmt = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

/** Registre des adhérents FTPE actifs (super admin) — synchronisé automatiquement, export CSV. */
export default function RegistreAdherentsFTPE() {
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [bientot, setBientot] = useState(false);
  const charger = useCallback(async (recherche = '', filtre = false) => {
    setBusy('liste');
    try { setData((await axios.get(API, { ...h(), params: { q: recherche, bientot: filtre } })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Registre indisponible'); }
    finally { setBusy(''); }
  }, []);
  useEffect(() => { charger(); }, [charger]);
  const basculer = () => { const v = !bientot; setBientot(v); charger(q, v); };
  const relancer = async (f) => {
    setBusy(`relance-${f.numero}`);
    try { const r = await axios.post(`${API}/${f.numero}/relance-renouvellement`, {}, h()); toast.success(`Rappel de renouvellement envoyé à ${r.data.envoye_a}`); charger(q, bientot); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Relance impossible'); } finally { setBusy(''); }
  };
  const exporter = async () => {
    setBusy('csv');
    try {
      const r = await axios.get(`${API}/export.csv`, { ...h(), params: { q }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data); a.download = `registre_adherents_ftpe_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    } catch { toast.error('Export impossible'); } finally { setBusy(''); }
  };
  return (
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="registre-ftpe">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-[#0a3d62]"><BadgeCheck className="h-5 w-5" /> Registre des adhérents FTPE</h2>
          <p className="text-xs text-slate-500">Adhérents actifs uniquement — mis à jour automatiquement à chaque ouverture.</p>
        </div>
        <Button size="sm" onClick={exporter} disabled={!!busy || !data?.total} className="bg-[#0a3d62] hover:bg-[#0a3d62]/90" data-testid="registre-ftpe-export-csv">
          {busy === 'csv' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Exporter en CSV
        </Button>
      </div>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); charger(q, bientot); }}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Raison sociale, SIRET, représentant, n° de registre…" className="pl-9" data-testid="registre-ftpe-recherche" />
        </div>
        <Button type="submit" variant="outline" data-testid="registre-ftpe-rechercher">Rechercher</Button>
      </form>
      {data?.expirent_bientot > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="registre-ftpe-alerte-echeances">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> {data.expirent_bientot} adhésion(s) expirent dans les 30 jours</p>
          <Button size="sm" variant="outline" onClick={basculer} className="border-amber-300 bg-white text-amber-800 hover:bg-amber-100" data-testid="registre-ftpe-filtre-echeances">
            {bientot ? 'Afficher tout' : 'Afficher seulement celles-là'}
          </Button>
        </div>
      )}
      <p className="text-xs text-slate-500" data-testid="registre-ftpe-total">{data ? `${data.total} adhérent(s) actif(s)` : 'Chargement…'}</p>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-3 py-2">N°</th><th className="px-3 py-2">Adhérent</th><th className="px-3 py-2">Contact</th><th className="px-3 py-2">Territoire</th><th className="px-3 py-2">Adhésion</th><th className="px-3 py-2">Expiration</th></tr>
          </thead>
          <tbody>
            {(data?.fiches || []).map((f) => (
              <tr key={f.numero} className="border-t border-slate-100 align-top hover:bg-slate-50/60" data-testid={`registre-ftpe-ligne-${f.numero}`}>
                <td className="px-3 py-2.5 font-mono text-xs text-[#0a3d62]">{f.numero}</td>
                <td className="px-3 py-2.5"><p className="font-semibold text-slate-800">{f.raison_sociale || f.representant || f.email || '—'}</p><p className="text-xs text-slate-500">{f.siret ? `SIRET ${f.siret}` : ''}{f.forme_juridique ? ` · ${f.forme_juridique}` : ''}</p></td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{f.representant || ''}<br />{[f.telephone, f.email].filter(Boolean).join(' · ')}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{f.territoire || '—'}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{fmt(f.date_adhesion)}</td>
                <td className="px-3 py-2.5 text-xs">
                  <span className={`font-semibold ${f.expire_bientot ? 'text-amber-700' : 'text-emerald-700'}`}>{fmt(f.date_expiration)}</span>
                  {f.expire_bientot && (
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`registre-ftpe-bientot-${f.numero}`}><AlertTriangle className="h-3 w-3" /> Expire bientôt</span>
                      <button type="button" onClick={() => relancer(f)} disabled={busy === `relance-${f.numero}`} title="Envoyer un rappel de renouvellement à l'adhérent"
                        className="inline-flex items-center gap-1 rounded-md border border-[#0a3d62]/30 px-2 py-0.5 text-[11px] font-semibold text-[#0a3d62] hover:bg-[#0a3d62]/5 disabled:opacity-50" data-testid={`registre-ftpe-relancer-${f.numero}`}>
                        {busy === `relance-${f.numero}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Relancer
                      </button>
                      {(f.relances_renouvellement || []).length > 0 && <span className="text-[10px] text-slate-400">relancé le {fmt(f.relances_renouvellement[f.relances_renouvellement.length - 1].at)}</span>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {data && !data.total && <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-400" data-testid="registre-ftpe-vide">Aucun adhérent FTPE actif.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
